// Libraries
import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
// Components
import { TextInput } from '../../../base/forms/TextInput';
// Actions
import ShowNotification from '../../../../actions/notification';
// Services
import { updateUserGoals } from '../../../../services/goals';
// Constants
import { NOTIFICATION_TYPE, DIALOG_STYLES } from '../../../../constants/constants';

// Local Constants
export const DEFAULT_UPDATE_GOALS = {
  dailyCallCap: '',
};

const UPDATE_GOALS_SCHEMA = () => Yup.object({
  dailyCallCap: Yup.number()
    .required('Required')
    .integer('The value must be an integer')
    .min(1, 'The minimum value is 1')
    .max(99, 'The maximum value is 99'),
});

const RELOAD_DATA = true;

export function UpdateGoals(props) {
  const {
    showNotification, isModalOpen, setIsModalOpen, selectedUser, tenantCallCap,
  } = props;

  const [goals, setGoals] = useState(DEFAULT_UPDATE_GOALS);
  const [isSaving, setIsSaving] = useState(false);

  const handleCloseModal = () => setIsModalOpen(false);

  const mapPreviousValues = (progressMap) => {
    let defaultValue = '';
    if (progressMap && progressMap.DAILY_CALL_CAP && progressMap.DAILY_CALL_CAP.goal) {
      defaultValue = progressMap.DAILY_CALL_CAP.goal;
    } else if (tenantCallCap) {
      defaultValue = tenantCallCap;
    }
    setGoals({ dailyCallCap: defaultValue });
  };

  const handleUpdateGoal = (values) => {
    const { refreshList } = props;
    setIsSaving(true);
    const newGoals = [{ goalTypeName: 'DAILY_CALL_CAP', count: values.dailyCallCap }];
    const updateUserGoalsRequest = updateUserGoals(selectedUser.id, newGoals);
    const updateUserGoalsPromise = updateUserGoalsRequest.promise;

    return updateUserGoalsPromise.then(() => {
      delete updateUserGoalsRequest.promise;
      setIsSaving(false);
      refreshList(RELOAD_DATA);
      handleCloseModal();
    }).catch((error) => {
      delete updateUserGoalsRequest.promise;
      setIsSaving(false);

      if (error.isCanceled || error.status === 401 || error.status === 403) {
        return;
      }
      showNotification({
        message: 'An error occurred while attempting to update the call cap',
        autoHide: true,
        notificationType: NOTIFICATION_TYPE.ERROR,
      });
    });
  };

  useEffect(() => {
    mapPreviousValues(selectedUser.progressMap);
  }, []);

  return (
    <Modal
      isOpen={isModalOpen}
      style={DIALOG_STYLES}
      onRequestClose={() => handleCloseModal()}
      contentLabel="Update Call Cap"
      data-test="UpdateGoals_onRequestClose"
    >
      <div className="simple-dialog medium-dialog">
        <div className="dialog-title">
          Update Call Cap
          <button
            type="button"
            className="close-icon i-close"
            onClick={() => handleCloseModal()}
            data-test="UpdateGoals_closeBtn"
          />
        </div>
        <div className="dialog-subtitle px-4 pt-2">
          {`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`}
        </div>
        <Formik
          validationSchema={UPDATE_GOALS_SCHEMA()}
          initialValues={goals}
          enableReinitialize
          onSubmit={(values) => {
            handleUpdateGoal(values);
          }}
          data-test="UpdateGoals_formikComponent"
        >
          {formik => (
            <Form onSubmit={(e) => e.preventDefault()}>
              <div className="text-left dialog-content">
                <div className="row">
                  <div className="col-6">
                    <TextInput
                      label="Daily CN Call Cap:"
                      name="dailyCallCap"
                      type="number"
                      min={1}
                      max={99}
                      maxLength={2}
                      onKeyDown={(e) => {
                        if (['e', 'E', '.', '-', '+'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      data-test="updateGoals_dailyCallCapTextBox"
                    />
                  </div>
                </div>
              </div>
              <div className="dialog-buttons justify-content-end px-4">
                <Button
                  variant="light"
                  onClick={() => handleCloseModal()}
                  data-test="UpdateGoals_cancelBtn"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="ml-2"
                  onClick={() => formik.handleSubmit()}
                  disabled={!formik.isValid || isSaving}
                  data-test="UpdateGoals_saveBtn"
                >
                  {isSaving ? 'Saving...' : 'Update Call Cap'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}

export function mapDispatchToProps(dispatch) {
  return {
    showNotification: notificationData => dispatch(ShowNotification(notificationData)),
  };
}

export default connect(null, mapDispatchToProps)(UpdateGoals);
